import { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Progress } from '../../components/ui/progress';
import { Button } from '../../components/ui/button';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  BookOpen, 
  Calendar,
  FileCheck,
  Clock,
  TrendingUp,
  Loader2,
  GraduationCap,
  ArrowRight,
  Handshake
} from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { t } from '../../lib/translations';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StudentDashboard = () => {
  const { user } = useAuth();
  const { lang } = useLanguage();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [courses, setCourses] = useState([]);
  const [documentStats, setDocumentStats] = useState({ validated: 0, total: 0 });
  const [documents, setDocuments] = useState([]);
  const [currentLevel, setCurrentLevel] = useState(null);
  const [partnerInfo, setPartnerInfo] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      const token = localStorage.getItem('hav_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      try {
        const [coursesRes, dossierRes, partnerRes] = await Promise.allSettled([
          axios.get(`${API_URL}/courses?status=active&limit=5`),
          axios.get(`${API_URL}/dossiers/self`, { headers }),
          axios.get(`${API_URL}/students/my-partner`, { headers }),
        ]);

        if (coursesRes.status === 'fulfilled') {
          setCourses(coursesRes.value.data);
        }

        if (dossierRes.status === 'fulfilled') {
          const d = dossierRes.value.data;
          setDocumentStats({ validated: d.validated_count, total: d.total_required });
          setDocuments(d.documents || []);
          setCurrentLevel(d.student_level || null);
        }

        if (partnerRes.status === 'fulfilled' && partnerRes.value.data?.partner) {
          setPartnerInfo(partnerRes.value.data);
        }
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const germanLevels = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];
  const currentLevelIndex = currentLevel ? germanLevels.indexOf(currentLevel) : -1;
  const progressPercent = currentLevelIndex >= 0 ? ((currentLevelIndex + 1) / germanLevels.length) * 100 : 0;
  const courseProgress = 0;
  const documentProgress = documentStats.total > 0 ? (documentStats.validated / documentStats.total) * 100 : 0;

  const nextCourse = courses.length > 0 ? {
    name: courses[0].name,
    date: "Lundi 28 Fév",
    time: "09:00 - 11:00",
    level: courses[0].level
  } : null;

  return (
    <div className="space-y-6" data-testid="student-dashboard">
      {/* Header avec nom */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-gray-900">
            Bonjour {user?.first_name} 👋
          </h1>
          <p className="text-gray-500 text-sm mt-1">{t('student_dash_progress', lang)}</p>
        </div>
      </div>

      {/* Stats Cards - 4 colonnes */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Niveau actuel */}
        <Card className="border-0 shadow-sm bg-gradient-to-br from-primary to-primary/80 text-white">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <GraduationCap className="w-6 h-6 opacity-80" />
              <span className="text-3xl font-bold">{currentLevel || '—'}</span>
            </div>
            <p className="text-white/80 text-sm font-medium">{t('student_dash_level', lang)}</p>
          </CardContent>
        </Card>

        {/* Progression cours */}
        <Card className="border-0 shadow-sm bg-gradient-to-br from-secondary to-secondary/80 text-white">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <TrendingUp className="w-6 h-6 opacity-80" />
              <span className="text-3xl font-bold">{courseProgress}%</span>
            </div>
            <p className="text-white/80 text-sm font-medium">{lang === 'de' ? 'Kurs abgeschlossen' : 'Cours complété'}</p>
          </CardContent>
        </Card>

        {/* Documents */}
        <Card className="border-0 shadow-sm bg-gradient-to-br from-accent to-accent/80 text-white">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <FileCheck className="w-6 h-6 opacity-80" />
              <span className="text-3xl font-bold">{documentStats.validated}/{documentStats.total}</span>
            </div>
            <p className="text-white/80 text-sm font-medium">{lang === 'de' ? 'Bestätigte Dokumente' : 'Documents validés'}</p>
          </CardContent>
        </Card>

        {/* Prochain cours */}
        <Card className="border-0 shadow-sm bg-gradient-to-br from-gray-700 to-gray-800 text-white">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <Calendar className="w-6 h-6 opacity-80" />
              <Clock className="w-5 h-5 opacity-60" />
            </div>
            <p className="text-white font-bold text-sm">{nextCourse?.date || 'Aucun'}</p>
            <p className="text-white/70 text-xs">{nextCourse?.time || '-'}</p>
          </CardContent>
        </Card>
      </div>

      {/* Section principale - 2 colonnes */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Progression détaillée */}
        <Card className="border-0 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-secondary/10 flex items-center justify-center">
                <GraduationCap className="w-5 h-5 text-secondary" />
              </div>
              <div>
                <h3 className="font-heading font-bold text-gray-900">Ma progression</h3>
                <p className="text-gray-500 text-sm">Objectif : Niveau B2</p>
              </div>
            </div>
            
            {/* Barre de niveaux */}
            <div className="space-y-4">
              <div className="flex justify-between items-center text-sm">
                <span className="text-gray-600">Progression globale</span>
                <span className="font-bold text-secondary">{Math.round(progressPercent)}%</span>
              </div>
              <Progress value={progressPercent} className="h-3" />
              <div className="flex justify-between text-xs">
                {germanLevels.map((level, index) => (
                  <span 
                    key={level} 
                    className={`px-2 py-1 rounded ${
                      currentLevelIndex >= 0 && index <= currentLevelIndex
                        ? 'bg-primary text-white font-bold' 
                        : 'text-gray-400'
                    }`}
                  >
                    {level}
                  </span>
                ))}
              </div>
            </div>

            {/* Cours actuel */}
            <div className="mt-6 pt-6 border-t">
              <div className="flex justify-between items-center text-sm mb-2">
                <span className="text-gray-600">
                  {currentLevel
                    ? `Cours actuel : Allemand ${currentLevel}`
                    : (lang === 'de' ? 'Noch kein Kurs' : 'Aucun cours assigné')}
                </span>
                <span className="font-bold">{courseProgress}%</span>
              </div>
              <Progress value={courseProgress} className="h-2" />
            </div>
          </CardContent>
        </Card>

        {/* État du dossier */}
        <Card className="border-0 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                  <FileCheck className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-heading font-bold text-gray-900">Mon dossier</h3>
                  <p className="text-gray-500 text-sm">Documents administratifs</p>
                </div>
              </div>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => navigate('/my-files')}
                className="text-primary"
                data-testid="view-documents-btn"
              >
                Voir <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>

            {/* Progression documents */}
            <div className="relative mb-6">
              <Progress value={documentProgress} className="h-4" />
              <span className="absolute inset-0 flex items-center justify-center text-xs font-bold text-white">
                {Math.round(documentProgress)}%
              </span>
            </div>

            {/* Liste documents */}
            <div className="space-y-3">
              {documents.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-4">
                  {lang === 'de' ? 'Keine Dokumente vorhanden' : 'Aucun document requis pour le moment'}
                </p>
              ) : (
                documents.map((doc, i) => (
                  <div key={i} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                    <span className={doc.status === 'validated' ? 'text-gray-900' : 'text-gray-400'}>{doc.label}</span>
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      doc.status === 'validated'
                        ? 'bg-green-100 text-green-700'
                        : doc.status === 'pending_review'
                        ? 'bg-yellow-100 text-yellow-700'
                        : doc.status === 'rejected'
                        ? 'bg-red-100 text-red-700'
                        : 'bg-gray-100 text-gray-500'
                    }`}>
                      {doc.status === 'validated'
                        ? (lang === 'de' ? 'Validiert' : 'Validé')
                        : doc.status === 'pending_review'
                        ? (lang === 'de' ? 'In Prüfung' : 'En cours')
                        : doc.status === 'rejected'
                        ? (lang === 'de' ? 'Abgelehnt' : 'Rejeté')
                        : (lang === 'de' ? 'Ausstehend' : 'En attente')}
                    </span>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Prochain cours - carte pleine largeur */}
      {nextCourse && (
        <Card className="border-0 shadow-sm bg-gradient-to-r from-secondary to-secondary/90 text-white overflow-hidden">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-xl bg-white/20 flex items-center justify-center">
                  <BookOpen className="w-7 h-7" />
                </div>
                <div>
                  <p className="text-white/70 text-sm">Prochain cours</p>
                  <h3 className="font-heading text-xl font-bold">{nextCourse.name}</h3>
                </div>
              </div>
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-white/70" />
                  <span className="font-medium">{nextCourse.date}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-white/70" />
                  <span className="font-medium">{nextCourse.time}</span>
                </div>
                <Button 
                  onClick={() => navigate('/my-courses')}
                  className="bg-white text-secondary hover:bg-white/90"
                  data-testid="go-to-course-btn"
                >
                  Accéder au cours
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Section Partenaire assigné */}
      {partnerInfo?.partner && (
        <Card className="border-0 shadow-sm border-l-4 border-l-primary" data-testid="partner-assigned-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Handshake className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Partenaire assigné</p>
                  <h3 className="font-heading font-bold text-gray-900">{partnerInfo.partner.organization_name}</h3>
                  <div className="flex items-center gap-2 mt-1">
                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                      partnerInfo.status === 'accepted' ? 'bg-green-100 text-green-700' :
                      partnerInfo.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-gray-100 text-gray-600'
                    }`}>
                      {partnerInfo.status === 'accepted' ? 'Accepté' :
                       partnerInfo.status === 'pending' ? 'En attente' : partnerInfo.status}
                    </span>
                    {partnerInfo.integration_stage && (
                      <span className="text-xs text-muted-foreground capitalize">
                        {partnerInfo.integration_stage.replace('_', ' ')}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              {partnerInfo.partner.country && (
                <span className="text-sm text-muted-foreground">{partnerInfo.partner.country}</span>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default StudentDashboard;
